// src/pages/admin/ProductsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, CheckCircle, XCircle, Trash2 } from 'lucide-react'
import { adminProductsApi } from '../../api/services'
import { Card, CardBody, StatusBadge, Button, PageSpinner, Pagination } from '../../components/common/ui'
import toast from 'react-hot-toast'

const TABS = [{k:'',l:'All'},{k:'pending_review',l:'Pending Review'},{k:'published',l:'Live'},{k:'rejected',l:'Rejected'},{k:'draft',l:'Draft'}]

export default function AdminProductsPage() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('pending_review')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['admin-products', status, search, page], queryFn: () => adminProductsApi.list({ status: status||undefined, search: search||undefined, page }) })
  const approve = useMutation({ mutationFn: id => adminProductsApi.approve(id), onSuccess: () => { toast.success('Approved & published'); qc.invalidateQueries(['admin-products']) } })
  const reject = useMutation({ mutationFn: ({id,reason}) => adminProductsApi.reject(id, { reason }), onSuccess: () => { toast.success('Rejected'); qc.invalidateQueries(['admin-products']) } })
  const del = useMutation({ mutationFn: id => adminProductsApi.delete(id), onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['admin-products']) } })

  const products = data?.data?.data?.products?.data || []
  const counts = data?.data?.data?.counts || {}
  const lastPage = data?.data?.data?.products?.last_page || 1

  return (
    <div>
      <h1 className="text-xl font-display font-bold mb-6">Products</h1>
      <div className="flex gap-2 mb-3 flex-wrap">
        {TABS.map(t => <button key={t.k} onClick={() => { setStatus(t.k); setPage(1) }} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${status===t.k?'bg-pink text-white border-pink':'border-border text-muted hover:border-pink'}`}>{t.l} {counts[t.k]!=null?`(${counts[t.k]})`:''}</button>)}
      </div>
      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input className="input pl-9 w-full" placeholder="Search products…" value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
      </div>
      {isLoading && <PageSpinner />}
      <div className="space-y-3">
        {products.map(p => (
          <Card key={p.id}>
            <CardBody className="flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-lg bg-surface border border-border overflow-hidden flex-shrink-0">
                {p.primary_image?.url && <img src={p.primary_image.url} alt={p.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{p.name}</span>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-xs text-muted">{p.supplier?.business_name || p.supplier?.name} · {p.category?.name}</p>
                <p className="text-sm text-pink font-bold">₹{Number(p.retail_price).toLocaleString('en-IN')}/m</p>
              </div>
              <div className="flex gap-2">
                {p.status === 'pending_review' && (
                  <>
                    <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate(p.id)}><CheckCircle size={13} /> Approve</Button>
                    <Button size="sm" variant="danger" loading={reject.isPending} onClick={() => reject.mutate({ id: p.id, reason: 'Does not meet quality standards.' })}><XCircle size={13} /></Button>
                  </>
                )}
                <Button size="sm" variant="ghost" loading={del.isPending} onClick={() => del.mutate(p.id)}><Trash2 size={13} /></Button>
              </div>
            </CardBody>
          </Card>
        ))}
        {!isLoading && products.length === 0 && <p className="text-center text-muted py-12">No products found.</p>}
      </div>
      {lastPage > 1 && <div className="mt-6"><Pagination currentPage={page} lastPage={lastPage} onPageChange={setPage} /></div>}
    </div>
  )
}
